import React, { useState, useEffect } from 'react';
import { useApp } from '../contexts/AppContext';
import { useTranslation } from '../hooks/useTranslation';
import { 
  SparklesIcon, 
  TrashIcon, 
  DocumentDuplicateIcon,
  XMarkIcon,
  CheckIcon,
  ArrowLeftIcon,
  XCircleIcon,
  Squares2X2Icon,
  SwatchIcon,
} from '@heroicons/react/24/outline';
import { 
  ColumnHeightOutlined, 
  ColumnWidthOutlined, 
  ExpandAltOutlined 
} from '@ant-design/icons';
import { applyFilterToImage } from '../utils/clutFilter';
import { isWebGLAvailable, isMobileDevice } from '../utils/webglHelper';
import CustomSlider from './CustomSlider';
import AdjustmentsControls from '../img-effects/adjustments/AdjustmentsControls';
import PaintingControls from './painting/PaintingControls';
import EffectSelector from '../img-effects/components/EffectSelector';
import GenericEffectControls from './effects/GenericEffectControls';

/**
 * Painel de controles mobile que aparece acima do footer
 * Mostra os controles baseado na aba ativa
 */
const MobileControlsPanel = ({ activeTab, onReset, onApply }) => {
  const { t } = useTranslation();
  
  const {
    selectedImageId,
    uploadedImages,
    baseLayer,
    updateImage,
    updateImageOpacity,
    updateImageBlendMode,
    applyImageTransform,
    duplicateImage,
    openDeleteConfirmation,
    setBaseLayerOpacity,
    setBaseLayerBackgroundColor,
    backendAvailable,
    removeBackgroundBackend,
    openAdvancedEditor,
    openEffectsPanel,
    setActiveEffectType,
    effectSettings,
    updateEffectSettings,
    applyEffect,
    selectedEffect,
    setSelectedEffect,
    activeEffectType,
    closeEffectsPanel,
    isSimplePaintingMode,
    setIsSimplePaintingMode,
    setPaintingSettings,
    // Estados para cores
    selectedColorFilter,
    setSelectedColorFilter,
    activeColorFilterType,
    setActiveColorFilterType,
    colorBlendMode,
    setColorBlendMode,
    colorOpacity,
    setColorOpacity,
    updateColorFilterSettings,
    applyColorFilter,
    openColorFiltersPanel,
  } = useApp();

  const selectedImage = uploadedImages.find(img => img.id === selectedImageId);
  const isBaseLayerSelected = selectedImageId === 'base-layer';

  // Verificar suporte WebGL
  const [webglSupported, setWebglSupported] = useState(true);
  const [showWebGLWarning, setShowWebGLWarning] = useState(false);

  useEffect(() => {
    const supported = isWebGLAvailable();
    setWebglSupported(supported);
    if (!supported && isMobileDevice()) {
      setShowWebGLWarning(true);
    }
  }, []);

  // Estados para filtros
  const [filtrosData, setFiltrosData] = useState(null);
  const [openCategories, setOpenCategories] = useState(new Set());
  const [applyingFilter, setApplyingFilter] = useState(false);
  const [filterThumbnails, setFilterThumbnails] = useState({});
  const [loadingThumbnails, setLoadingThumbnails] = useState(new Set());
  const [selectedFilter, setSelectedFilter] = useState(null);
  const [previewingFilter, setPreviewingFilter] = useState(null);
  const [originalImageBeforeFilter, setOriginalImageBeforeFilter] = useState(null);

  // Estado para tipo de blend mode ativo (mobile)
  const [activeColorBlendType, setActiveColorBlendType] = useState(null);
  
  // Estado para controle de opacidade de cores no mobile
  const [mobileColorOpacity, setMobileColorOpacity] = useState(70);
  const [selectedColorForAdjust, setSelectedColorForAdjust] = useState(null); // Qual cor está sendo ajustada
  
  // Estados para pintura
  const [paintBrushSize, setPaintBrushSize] = useState(20);
  const [paintBrushColor, setPaintBrushColor] = useState('#000000');
  const [paintBrushOpacity, setPaintBrushOpacity] = useState(100);

  // Estado para grupo de ajustes ativo (mobile)
  const [activeAdjustmentGroup, setActiveAdjustmentGroup] = useState(null);

  // Resetar grupo ativo quando mudar de aba
  useEffect(() => {
    setActiveAdjustmentGroup(null);
  }, [activeTab]);

  const [paintBrushFlow, setPaintBrushFlow] = useState(6);
  const [paintBrushSpacing, setPaintBrushSpacing] = useState(2);
  const [paintBrushAngle, setPaintBrushAngle] = useState(0);
  const [paintBrushRotate, setPaintBrushRotate] = useState(false);
  const [paintCurrentTool, setPaintCurrentTool] = useState('pen');
  const [paintStabilizerLevel, setPaintStabilizerLevel] = useState(10);
  const [paintStabilizerWeight, setPaintStabilizerWeight] = useState(50);
  const [paintCurrentBrushImage, setPaintCurrentBrushImage] = useState(null);
  const [paintBlendMode, setPaintBlendMode] = useState('source-over');

  // Constantes para filtros de cores
  const COLOR_FILTERS = [
    { id: 'sunset', name: 'Pôr do Sol', colors: ['#FF6B6B', '#FFE66D'], angle: 135 },
    { id: 'ocean', name: 'Oceano', colors: ['#667eea', '#764ba2'], angle: 135 },
    { id: 'forest', name: 'Floresta', colors: ['#0F2027', '#203A43', '#2C5364'], angle: 135 },
    { id: 'purple-dream', name: 'Sonho Roxo', colors: ['#C471F5', '#FA71CD'], angle: 135 },
    { id: 'fire', name: 'Fogo', colors: ['#f12711', '#f5af19'], angle: 135 },
    { id: 'mint', name: 'Menta', colors: ['#00b09b', '#96c93d'], angle: 135 },
    { id: 'pink', name: 'Rosa', colors: ['#FF69B4', '#FFB6C1'], angle: 135 },
    { id: 'sky', name: 'Céu', colors: ['#56CCF2', '#2F80ED'], angle: 135 },
    { id: 'gold', name: 'Ouro', colors: ['#FFD700', '#FFA500'], angle: 135 },
    { id: 'dusk', name: 'Entardecer', colors: ['#FF512F', '#DD2476'], angle: 135 },
    { id: 'raspberry-blue', name: 'Framboesa Azul', colors: ['#E94057', '#8A2387'], angle: 135 },
    { id: 'peach', name: 'Pêssego', colors: ['#FFECD2', '#FCB69F'], angle: 135 },
  ];
  
  // Estados para thumbnails de cores
  const [colorThumbnails, setColorThumbnails] = useState({});
  const [loadingColorThumbnails, setLoadingColorThumbnails] = useState(new Set());


  // Carregar dados dos filtros analógicos
  useEffect(() => {
    const loadFiltrosData = async () => {
      try {
        const response = await fetch('/lotie/filtros.json');
        if (response.ok) {
          const data = await response.json();
          setFiltrosData(data);
        }
      } catch (error) {
        console.error('Erro ao carregar dados dos filtros:', error);
      }
    };
    loadFiltrosData();
  }, []);

  // Inicializar painel de efeitos para ajustes
  useEffect(() => {
    if (activeTab === 'ajustes' && selectedImageId && !isBaseLayerSelected) {
      openEffectsPanel(selectedImageId);
      setActiveEffectType('adjustments');
    }
  }, [activeTab, selectedImageId, isBaseLayerSelected, openEffectsPanel, setActiveEffectType]);

  // Inicializar painel de efeitos quando mudar para aba de efeitos
  useEffect(() => {
    if (activeTab === 'efeitos' && selectedImageId && !isBaseLayerSelected) {
      openEffectsPanel(selectedImageId);
      if (activeEffectType === 'adjustments') {
        setActiveEffectType(null);
      }
    }
  }, [activeTab, selectedImageId, isBaseLayerSelected, openEffectsPanel, setActiveEffectType, activeEffectType]);

  const handleImageTransform = (type) => {
    if (selectedImageId && !isBaseLayerSelected) {
      applyImageTransform(selectedImageId, type);
    }
  };

  // Funções para filtros
  const toggleCategory = (categoryName) => {
    setOpenCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryName)) {
        newSet.delete(categoryName);
      } else {
        newSet.add(categoryName);
      }
      return newSet;
    });
  };

  // Gerar thumbnail de preview do filtro
  const generateFilterThumbnail = async (filtro, sourceImage) => {
    const thumbnailKey = `${selectedImageId}-${filtro.caminho}`;
    
    // Se já está carregando ou já existe, não fazer nada
    if (loadingThumbnails.has(thumbnailKey) || filterThumbnails[thumbnailKey]) {
      return;
    }
    
    // Marcar como carregando
    setLoadingThumbnails(prev => new Set(prev).add(thumbnailKey));
    
    try {
      // Criar mini versão da imagem (120x120 para mobile)
      const tempCanvas = document.createElement('canvas');
      const size = 120;
      tempCanvas.width = size;
      tempCanvas.height = size;
      const tempCtx = tempCanvas.getContext('2d');
      
      // Calcular dimensões mantendo aspect ratio
      const scale = Math.min(size / sourceImage.width, size / sourceImage.height);
      const scaledWidth = sourceImage.width * scale;
      const scaledHeight = sourceImage.height * scale;
      const offsetX = (size - scaledWidth) / 2;
      const offsetY = (size - scaledHeight) / 2;
      
      tempCtx.drawImage(sourceImage, offsetX, offsetY, scaledWidth, scaledHeight);
      
      // Criar elemento de imagem temporário
      const miniImg = new Image();
      miniImg.src = tempCanvas.toDataURL();
      
      await new Promise((resolve) => {
        miniImg.onload = resolve;
      });
      
      // Aplicar filtro na mini imagem
      const clutUrl = `/lotie/${filtro.caminho}`;
      const filteredUrl = await applyFilterToImage(miniImg, clutUrl, 1.0);
      
      // Salvar thumbnail
      setFilterThumbnails(prev => ({
        ...prev,
        [thumbnailKey]: filteredUrl
      }));
    } catch (error) {
      console.error('Erro ao gerar thumbnail:', error);
    } finally {
      // Remover do set de carregamento
      setLoadingThumbnails(prev => {
        const newSet = new Set(prev);
        newSet.delete(thumbnailKey);
        return newSet;
      });
    }
  };

  // Gerar thumbnails quando a imagem ou filtros mudarem
  useEffect(() => {
    if (!selectedImage || !filtrosData || selectedImageId === 'base-layer') return;
    
    const sourceImage = selectedImage.originalImage || selectedImage.image;
    if (!sourceImage) return;
    
    // Limpar thumbnails antigos se mudou de imagem
    setFilterThumbnails({});
    setLoadingThumbnails(new Set());
    
    // Gerar thumbnails para todos os filtros (assíncrono)
    const generateAll = async () => {
      for (const categoria of filtrosData.categorias.filter(cat => cat.nome.toLowerCase() !== 'none')) {
        for (const filtro of categoria.filtros) {
          await generateFilterThumbnail(filtro, sourceImage);
        }
      }
    };
    
    generateAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedImageId, filtrosData]);

  const handleApplyFilter = async (filtro) => {
    if (!selectedImageId || selectedImageId === 'base-layer') {
      console.log('❌ Nenhuma imagem selecionada ou base layer selecionada');
      return;
    }
    const selectedImage = uploadedImages.find(img => img.id === selectedImageId);
    if (!selectedImage) {
      console.log('❌ Imagem não encontrada');
      return;
    }

    console.log('🎨 [FILTRO] Iniciando aplicação:', filtro.nome, 'Caminho:', filtro.caminho);
    console.log('🎨 [FILTRO] Imagem selecionada:', selectedImage.name);
    setApplyingFilter(true);

    try {
      // Salvar estado original antes de aplicar (se ainda não foi salvo)
      if (!previewingFilter) {
        setOriginalImageBeforeFilter({
          ...selectedImage,
          image: selectedImage.image,
          originalImage: selectedImage.originalImage,
          analogFilter: selectedImage.analogFilter,
          analogFilterPath: selectedImage.analogFilterPath
        });
      }
      
      const clutUrl = `/lotie/${filtro.caminho}`;
      console.log('🔗 URL do filtro:', clutUrl);
      
      // Usar imagem original se existir, caso contrário usar imagem atual
      const sourceImage = selectedImage.originalImage || selectedImage.image;
      console.log('🎨 [FILTRO] Imagem fonte:', sourceImage.width, 'x', sourceImage.height);
      console.log('🎨 [FILTRO] URL CLUT:', clutUrl);
      
      const filteredImageUrl = await applyFilterToImage(sourceImage, clutUrl, 1.0);
      console.log('✅ [FILTRO] Filtro aplicado com sucesso, URL gerada');
      
      // Criar nova imagem com o filtro aplicado
      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      img.onload = () => {
        console.log('✅ [FILTRO] Imagem carregada, atualizando canvas');
        console.log('✅ [FILTRO] Nova imagem:', img.width, 'x', img.height, 'complete:', img.complete);
        
        // Forçar re-render criando novo objeto de imagem
        const newImg = new Image();
        newImg.crossOrigin = 'anonymous';
        newImg.onload = () => {
          updateImage(selectedImageId, {
            ...selectedImage,
            image: newImg,
            originalImage: selectedImage.originalImage || selectedImage.image, // Preservar original
            analogFilter: filtro.nome,
            analogFilterPath: filtro.caminho,
            _timestamp: Date.now() // Forçar detecção de mudança
          });
          setPreviewingFilter(filtro);
          setSelectedFilter(filtro);
          setApplyingFilter(false);
          console.log('✅ [FILTRO] Filtro em preview - CONCLUÍDO');
        };
        newImg.src = filteredImageUrl;
      };
      
      img.onerror = (error) => {
        console.error('❌ [FILTRO] Erro ao carregar imagem filtrada:', error);
        setApplyingFilter(false);
        alert('Erro ao aplicar filtro. Verifique o console.');
      };
      
      img.src = filteredImageUrl;
    } catch (error) {
      console.error('❌ Erro ao aplicar filtro:', error);
      setApplyingFilter(false);
    }
  };

  // Gerar thumbnail de preview do filtro de cor
  const generateColorThumbnail = async (colorFilter, sourceImage, blendMode) => {
    const thumbnailKey = `${selectedImageId}-${colorFilter.id}-${blendMode}`;
    
    if (loadingColorThumbnails.has(thumbnailKey) || colorThumbnails[thumbnailKey]) {
      return;
    }
    
    setLoadingColorThumbnails(prev => new Set(prev).add(thumbnailKey));
    
    try {
      const tempCanvas = document.createElement('canvas');
      const size = 120;
      tempCanvas.width = size;
      tempCanvas.height = size;
      const tempCtx = tempCanvas.getContext('2d');
      
      const scale = Math.min(size / sourceImage.width, size / sourceImage.height);
      const scaledWidth = sourceImage.width * scale;
      const scaledHeight = sourceImage.height * scale;
      const offsetX = (size - scaledWidth) / 2;
      const offsetY = (size - scaledHeight) / 2;
      
      tempCtx.drawImage(sourceImage, offsetX, offsetY, scaledWidth, scaledHeight);
      
      // Aplicar gradiente com o blend mode especificado
      tempCtx.globalCompositeOperation = blendMode;
      tempCtx.globalAlpha = 0.7; // 70% padrão para mobile
      
      const gradient = tempCtx.createLinearGradient(0, 0, size, size);
      colorFilter.colors.forEach((color, idx) => {
        gradient.addColorStop(idx / (colorFilter.colors.length - 1), color);
      });
      
      tempCtx.fillStyle = gradient;
      tempCtx.fillRect(0, 0, size, size);
      
      const thumbnailUrl = tempCanvas.toDataURL();
      setColorThumbnails(prev => ({
        ...prev,
        [thumbnailKey]: thumbnailUrl
      }));
    } catch (error) {
      console.error('Erro ao gerar thumbnail de cor:', error);
    } finally {
      setLoadingColorThumbnails(prev => {
        const newSet = new Set(prev);
        newSet.delete(thumbnailKey);
        return newSet;
      });
    }
  };

  // Gerar thumbnails de cores quando a imagem mudar
  useEffect(() => {
    if (!selectedImage || selectedImageId === 'base-layer' || activeTab !== 'cores') return;
    
    const sourceImage = selectedImage.originalImage || selectedImage.image;
    if (!sourceImage) return;
    
    setColorThumbnails({});
    setLoadingColorThumbnails(new Set());
    
    // Gerar thumbnails para todas as combinações (cor × blend mode)
    const blendModes = ['multiply', 'overlay', 'color'];
    COLOR_FILTERS.forEach(colorFilter => {
      blendModes.forEach(blendMode => {
        generateColorThumbnail(colorFilter, sourceImage, blendMode);
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedImageId, activeTab]);

  const handleSelectColorFilter = (colorFilter, blendMode = null) => {
    setSelectedColorFilter(colorFilter);
    
    // Se blend mode foi passado, atualizar
    if (blendMode) {
      setColorBlendMode(blendMode);
    }
    
    // No mobile, sempre usar 70% de opacidade inicialmente
    const opacityToUse = mobileColorOpacity;
    setColorOpacity(opacityToUse);
    
    if (selectedImageId && selectedImageId !== 'base-layer') {
      openColorFiltersPanel(selectedImageId);
      setActiveColorFilterType('gradient');
      updateColorFilterSettings('gradient', {
        colors: colorFilter.colors,
        angle: colorFilter.angle || 135,
        opacity: opacityToUse,
        blendMode: blendMode || colorBlendMode
      });
    }
  };

  if (!selectedImageId) {
    return (
      <div className="md:hidden border-t border-gray-800" style={{ backgroundColor: '#131714' }}>
        <div className="p-4 text-center">
          <p className="text-sm text-gray-300">{t('layers.noLayerSelected')}</p>
        </div>
      </div>
    );
  }

  // Mapa de nomes das abas
  const tabNames = {
    'geral': 'Geral',
    'ajustes': 'Ajustes',
    'filtros': 'Filtros',
    'cores': 'Cores',
    'efeitos': 'Efeitos',
  };

  return (
    <div className="md:hidden border-t border-gray-800 flex flex-col" style={{ backgroundColor: '#131714' }}>
      {/* Barra de ações (X | Nome | ✓) - Apenas quando não for "geral" */}
      {activeTab !== 'geral' && (
        <div className="border-b border-gray-800 flex items-center justify-between px-3 py-1.5" style={{ backgroundColor: '#131714' }}>
          <button
            onClick={() => {
              // Se estiver em ajustes e tiver um grupo ativo, voltar aos ícones
              if (activeTab === 'ajustes' && activeAdjustmentGroup) {
                setActiveAdjustmentGroup(null);
              } else if (activeTab === 'cores' && selectedColorForAdjust) {
                // Se estiver em cores e tiver uma cor selecionada, voltar ao scroll
                setSelectedColorForAdjust(null);
              } else {
                onReset();
              }
            }}
            className="flex items-center justify-center w-8 h-8 rounded-lg hover:bg-gray-800 transition-colors"
            title={(activeTab === 'ajustes' && activeAdjustmentGroup) || (activeTab === 'cores' && selectedColorForAdjust) ? "Voltar" : "Resetar"}
          >
            {((activeTab === 'ajustes' && activeAdjustmentGroup) || (activeTab === 'cores' && selectedColorForAdjust)) ? (
              <ArrowLeftIcon className="w-5 h-5 text-[#7eb715]" />
            ) : (
              <XMarkIcon className="w-5 h-5 text-[#7eb715]" />
            )}
          </button>
          <div className="flex-1 text-center">
            <span className="text-xs font-semibold text-[#7eb715]">{tabNames[activeTab]}</span>
          </div>
          <button
            onClick={onApply}
            className="flex items-center justify-center w-8 h-8 rounded-lg hover:bg-gray-800 transition-colors"
            title="Aplicar"
          >
            <CheckIcon className="w-5 h-5 text-[#7eb715]" />
          </button>
        </div>
      )}
      
      {/* Área de conteúdo dos controles - Sempre visível com altura fixa */}
      <div className="overflow-y-auto text-white" style={{ backgroundColor: '#131714', touchAction: 'pan-y', minHeight: '26vh', maxHeight: '26vh' }}>
        <div className={`py-4 ${(activeTab === 'filtros' || activeTab === 'cores') ? 'px-0' : 'px-8'}`}>
          {/* ABA GERAL */}
          {activeTab === 'geral' && (
            <div className="space-y-4">
              {isBaseLayerSelected ? (
                <>
                  <div>
                    <label className="block text-xs font-medium text-gray-200 mb-1.5">Cor de Fundo</label>
                    <input
                      type="color"
                      value={baseLayer.backgroundColor}
                      onChange={(e) => setBaseLayerBackgroundColor(e.target.value)}
                      className="w-full h-10 border border-gray-600 rounded cursor-pointer bg-gray-800"
                    />
                  </div>
                  <CustomSlider
                    min={0}
                    max={100}
                    step={1}
                    value={baseLayer.opacity * 100}
                    onChange={(val) => setBaseLayerOpacity(val / 100)}
                    label="Opacidade"
                    formatValue={(val) => `${Math.round(val)}%`}
                  />
                </>
              ) : selectedImage ? (
                <>
                  <CustomSlider
                    min={0}
                    max={100}
                    step={1}
                    value={(selectedImage.opacity || 1) * 100}
                    onChange={(val) => updateImageOpacity(selectedImageId, val / 100)}
                    label="Opacidade"
                    formatValue={(val) => `${Math.round(val)}%`}
                  />
                  
                  <div>
                    <label className="block text-xs font-medium text-gray-200 mb-1.5">Modo de Mesclagem</label>
                    <select
                      value={selectedImage.blendMode || 'normal'}
                      onChange={(e) => updateImageBlendMode(selectedImageId, e.target.value)}
                      className="w-full px-3 py-2 border border-gray-600 rounded-md text-sm bg-gray-800 text-white"
                    >
                      <option value="normal">Normal</option>
                      <option value="multiply">Multiplicar</option>
                      <option value="screen">Tela</option>
                      <option value="overlay">Sobrepor</option>
                      <option value="darken">Escurecer</option>
                      <option value="lighten">Clarear</option>
                      <option value="color-dodge">Subexposição de Cor</option>
                      <option value="color-burn">Superexposição de Cor</option>
                      <option value="hard-light">Luz Forte</option>
                      <option value="soft-light">Luz Suave</option>
                      <option value="difference">Diferença</option>
                      <option value="exclusion">Exclusão</option>
                      <option value="hue">Matiz</option>
                      <option value="saturation">Saturação</option>
                      <option value="color">Cor</option>
                      <option value="luminosity">Luminosidade</option>
                    </select>
                  </div>

                  <div className="border-t border-gray-600 pt-4 mt-4">
                    <label className="block text-xs font-medium text-gray-200 mb-2">Transformações</label>
                    <div className="grid grid-cols-3 gap-2">
                      <button
                        onClick={() => handleImageTransform('flip-horizontal')}
                        className="flex flex-col items-center justify-center p-2 bg-gray-800 hover:bg-gray-700 rounded transition-colors border border-gray-600"
                        title="Espelhar Horizontalmente"
                      >
                        <ColumnWidthOutlined className="text-lg text-[#7eb715]" />
                        <span className="text-[10px] text-gray-300 mt-1">Espelhar H</span>
                      </button>
                      <button
                        onClick={() => handleImageTransform('flip-vertical')}
                        className="flex flex-col items-center justify-center p-2 bg-gray-800 hover:bg-gray-700 rounded transition-colors border border-gray-600"
                        title="Espelhar Verticalmente"
                      >
                        <ColumnHeightOutlined className="text-lg text-[#7eb715]" />
                        <span className="text-[10px] text-gray-300 mt-1">Espelhar V</span>
                      </button>
                      <button
                        onClick={() => handleImageTransform('rotate-90')}
                        className="flex flex-col items-center justify-center p-2 bg-gray-800 hover:bg-gray-700 rounded transition-colors border border-gray-600"
                        title="Rotacionar 90°"
                      >
                        <ExpandAltOutlined className="text-lg text-[#7eb715]" />
                        <span className="text-[10px] text-gray-300 mt-1">Girar 90°</span>
                      </button>
                    </div>
                  </div>

                  {backendAvailable && (
                    <div className="border-t border-gray-600 pt-4 mt-4">
                      <label className="block text-xs font-medium text-gray-200 mb-2">Remoção de Fundo (IA)</label>
                      <button
                        onClick={() => removeBackgroundBackend(selectedImageId)}
                        className="w-full flex items-center justify-center space-x-2 px-4 py-2.5 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg hover:from-green-700 hover:to-green-800 transition-all shadow-sm"
                      >
                        <SparklesIcon className="w-4 h-4" />
                        <span className="text-sm font-medium">Remover Fundo com IA</span>
                      </button>
                    </div>
                  )}

                  <div className="border-t border-gray-600 pt-4 mt-4">
                    <label className="block text-xs font-medium text-gray-200 mb-2">Ações</label>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        onClick={() => duplicateImage(selectedImageId)}
                        className="flex items-center justify-center space-x-2 px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors border border-blue-500"
                      >
                        <DocumentDuplicateIcon className="w-4 h-4" />
                        <span className="text-xs font-medium">Duplicar</span>
                      </button>
                      <button
                        onClick={() => openDeleteConfirmation(selectedImage)}
                        className="flex items-center justify-center space-x-2 px-3 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors border border-red-500"
                      >
                        <TrashIcon className="w-4 h-4" />
                        <span className="text-xs font-medium">Excluir</span>
                      </button>
                    </div>
                  </div>
                </>
              ) : null}
            </div>
          )}

          {/* ABA AJUSTES */}
          {activeTab === 'ajustes' && selectedImage && !isBaseLayerSelected && (
            <div className="space-y-4">
              <AdjustmentsControls
                isMobile={true}
                activeGroup={activeAdjustmentGroup}
                onGroupChange={setActiveAdjustmentGroup}
                settings={effectSettings?.adjustments || {}}
                onUpdate={(newSettings) => {
                  updateEffectSettings('adjustments', newSettings);
                }}
                onApply={() => {
                  applyEffect('adjustments');
                }}
              />
            </div>
          )}

          {/* ABA FILTROS */}
          {activeTab === 'filtros' && selectedImage && !isBaseLayerSelected && (
            <div>
              {applyingFilter && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                  <div className="bg-gray-800 rounded-lg p-4 shadow-xl border border-gray-600">
                    <div className="flex items-center space-x-3">
                      <svg className="animate-spin h-5 w-5 text-[#7eb715]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <p className="text-sm font-medium text-white">Aplicando filtro...</p>
                    </div>
                  </div>
                </div>
              )}

              {filtrosData ? (
                <div>
                  {/* Scroll horizontal contínuo com todos os filtros */}
                  <div className="overflow-x-auto overflow-y-hidden pb-2" style={{ 
                    scrollbarWidth: 'thin',
                    scrollbarColor: '#7eb715 #1f2937'
                  }}>
                    <div className="flex gap-3 px-4" style={{ minWidth: 'min-content' }}>
                      {filtrosData.categorias
                        .filter((categoria) => categoria.nome.toLowerCase() !== 'none')
                        .flatMap((categoria) => categoria.filtros)
                        .map((filtro) => {
                          const isSelected = selectedFilter?.caminho === filtro.caminho;
                          const thumbnailKey = `${selectedImageId}-${filtro.caminho}`;
                          const thumbnailUrl = filterThumbnails[thumbnailKey];
                          const isLoadingThumb = loadingThumbnails.has(thumbnailKey);
                          
                          return (
                            <button
                              key={filtro.caminho}
                              onClick={() => handleApplyFilter(filtro)}
                              disabled={applyingFilter}
                              className={`flex-shrink-0 relative rounded-lg overflow-hidden border-2 transition-all disabled:opacity-50 ${
                                isSelected ? 'border-[#7eb715] ring-2 ring-[#7eb715]' : 'border-gray-600 hover:border-[#7eb715]'
                              }`}
                              style={{ width: '80px', height: '130px', boxSizing: 'border-box' }}
                            >
                              <div className="absolute inset-0 bg-gray-800 flex items-center justify-center overflow-hidden">
                                {thumbnailUrl ? (
                                  <img 
                                    src={thumbnailUrl} 
                                    alt={filtro.nome}
                                    style={{ 
                                      width: 'auto',
                                      height: '120%',
                                      minWidth: '100%',
                                      objectFit: 'cover',
                                      display: 'block'
                                    }}
                                  />
                                ) : isLoadingThumb ? (
                                  <div className="absolute inset-0 flex items-center justify-center">
                                    <svg className="animate-spin h-6 w-6 text-[#7eb715]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                  </div>
                                ) : (
                                  <div className="absolute inset-0 flex items-center justify-center">
                                    <span className="text-xs text-gray-300 px-2 text-center leading-tight">{filtro.nome}</span>
                                  </div>
                                )}
                                
                                {/* Nome do filtro em overlay */}
                                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent p-1.5">
                                  <p className="text-white text-[10px] font-medium text-center leading-tight">{filtro.nome}</p>
                                </div>
                              </div>
                            </button>
                          );
                        })}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center py-8">
                  <div className="text-center">
                    <svg className="animate-spin h-8 w-8 mx-auto mb-2 text-[#7eb715]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <p className="text-sm text-gray-300">Carregando filtros...</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ABA CORES */}
          {activeTab === 'cores' && selectedImage && !isBaseLayerSelected && (
            <div>
              {selectedColorForAdjust ? (
                // Mostrar apenas slider de intensidade
                <div className="px-8">
                  {/* Slider de intensidade */}
                  <div className="range-slider-wrapper">
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm font-medium text-gray-200">Intensidade</label>
                      <span className="text-sm font-semibold text-[#7eb715] bg-gray-800 px-2 py-1 rounded-md">
                        {Math.round(mobileColorOpacity)}%
                      </span>
                    </div>
                    <div className="range-slider range-slider-horizontal" style={{ width: '100%' }}>
                      <div className="range-bar">
                        <div 
                          className="range-bar-active" 
                          style={{ 
                            width: `${mobileColorOpacity}%`,
                            backgroundColor: '#7eb715'
                          }}
                        ></div>
                      </div>
                      <div 
                        className="range-knob-wrap range-knob-active-state" 
                        style={{ left: `${mobileColorOpacity}%` }}
                        onMouseDown={(e) => {
                          const startX = e.clientX;
                          const startValue = mobileColorOpacity;
                          
                          const handleMove = (moveEvent) => {
                            const currentX = moveEvent.clientX;
                            const deltaX = currentX - startX;
                            const sliderWidth = 280;
                            const deltaValue = (deltaX / sliderWidth) * 100;
                            const newValue = Math.max(0, Math.min(100, startValue + deltaValue));
                            const steppedValue = Math.round(newValue);
                            
                            setMobileColorOpacity(steppedValue);
                            setColorOpacity(steppedValue);
                            if (activeColorFilterType === 'gradient' && selectedColorFilter) {
                              updateColorFilterSettings('gradient', {
                                colors: selectedColorFilter.colors,
                                angle: selectedColorFilter.angle || 135,
                                opacity: steppedValue,
                                blendMode: colorBlendMode
                              });
                            }
                          };
                          
                          const handleEnd = () => {
                            document.removeEventListener('mousemove', handleMove);
                            document.removeEventListener('mouseup', handleEnd);
                            document.removeEventListener('touchmove', handleMove);
                            document.removeEventListener('touchend', handleEnd);
                          };
                          
                          document.addEventListener('mousemove', handleMove);
                          document.addEventListener('mouseup', handleEnd);
                          document.addEventListener('touchmove', handleMove);
                          document.addEventListener('touchend', handleEnd);
                        }}
                        onTouchStart={(e) => {
                          e.stopPropagation();
                          const startX = e.touches[0].clientX;
                          const startValue = mobileColorOpacity;
                          
                          const handleMove = (moveEvent) => {
                            moveEvent.preventDefault();
                            const currentX = moveEvent.touches[0].clientX;
                            const deltaX = currentX - startX;
                            const sliderWidth = 280;
                            const deltaValue = (deltaX / sliderWidth) * 100;
                            const newValue = Math.max(0, Math.min(100, startValue + deltaValue));
                            const steppedValue = Math.round(newValue);
                            
                            setMobileColorOpacity(steppedValue);
                            setColorOpacity(steppedValue);
                            if (activeColorFilterType === 'gradient' && selectedColorFilter) {
                              updateColorFilterSettings('gradient', {
                                colors: selectedColorFilter.colors,
                                angle: selectedColorFilter.angle || 135,
                                opacity: steppedValue,
                                blendMode: colorBlendMode
                              });
                            }
                          };
                          
                          const handleEnd = () => {
                            document.removeEventListener('mousemove', handleMove);
                            document.removeEventListener('mouseup', handleEnd);
                            document.removeEventListener('touchmove', handleMove);
                            document.removeEventListener('touchend', handleEnd);
                          };
                          
                          document.addEventListener('mousemove', handleMove);
                          document.addEventListener('mouseup', handleEnd);
                          document.addEventListener('touchmove', handleMove);
                          document.addEventListener('touchend', handleEnd);
                        }}
                      >
                        <div className="range-knob"></div>
                        <div className="range-knob-label">{Math.round(mobileColorOpacity)}</div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                // Scroll horizontal com todas as combinações (cor × blend mode)
                <div className="overflow-x-auto overflow-y-hidden pb-2" style={{ 
                  scrollbarWidth: 'thin',
                  scrollbarColor: '#7eb715 #1f2937'
                }}>
                  <div className="flex gap-3 px-4" style={{ minWidth: 'min-content' }}>
                    {COLOR_FILTERS.flatMap((colorFilter) => {
                      // Para cada cor, criar 3 variações (uma para cada blend mode)
                      const blendModes = [
                        { id: 'multiply', name: 'Multiplicar', Icon: XCircleIcon },
                        { id: 'overlay', name: 'Sobreposição', Icon: Squares2X2Icon },
                        { id: 'color', name: 'Cor', Icon: SwatchIcon }
                      ];
                      
                      return blendModes.map((blendMode) => {
                        const combinedId = `${colorFilter.id}-${blendMode.id}`;
                        const isSelected = selectedColorFilter?.id === colorFilter.id && colorBlendMode === blendMode.id;
                        const thumbnailKey = `${selectedImageId}-${colorFilter.id}-${blendMode.id}`;
                        const thumbnailUrl = colorThumbnails[thumbnailKey];
                        const isLoadingThumb = loadingColorThumbnails.has(thumbnailKey);
                        const Icon = blendMode.Icon;
                        
                        return (
                          <button
                            key={combinedId}
                            onClick={() => {
                              // Ao clicar, mostrar o slider de intensidade
                              setSelectedColorForAdjust({ colorFilter, blendMode });
                              handleSelectColorFilter(colorFilter, blendMode.id);
                            }}
                            disabled={applyingFilter}
                            className={`flex-shrink-0 relative rounded-lg overflow-hidden border-2 transition-all disabled:opacity-50 ${
                              isSelected ? 'border-[#7eb715] ring-2 ring-[#7eb715]' : 'border-gray-600 hover:border-[#7eb715]'
                            }`}
                            style={{ width: '80px', height: '130px', boxSizing: 'border-box' }}
                          >
                            <div className="absolute inset-0 bg-gray-800 flex items-center justify-center overflow-hidden">
                              {thumbnailUrl ? (
                                <img 
                                  src={thumbnailUrl} 
                                  alt={`${colorFilter.name} - ${blendMode.name}`}
                                  style={{ 
                                    width: 'auto',
                                    height: '120%',
                                    minWidth: '100%',
                                    objectFit: 'cover',
                                    display: 'block'
                                  }}
                                />
                              ) : isLoadingThumb ? (
                                <div className="absolute inset-0 flex items-center justify-center">
                                  <svg className="animate-spin h-6 w-6 text-[#7eb715]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                  </svg>
                                </div>
                              ) : (
                                <div className="absolute inset-0 flex items-center justify-center">
                                  <span className="text-xs text-gray-300 px-2 text-center leading-tight">{colorFilter.name}</span>
                                </div>
                              )}
                              
                              {/* Ícone do blend mode no topo */}
                              <div className="absolute top-1 right-1 bg-black/70 rounded-full p-1">
                                <Icon className="w-4 h-4 text-[#7eb715]" />
                              </div>
                              
                              {/* Nome do filtro em overlay */}
                              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent p-1.5">
                                <p className="text-white text-[10px] font-medium text-center leading-tight">{colorFilter.name}</p>
                              </div>
                            </div>
                          </button>
                        );
                      });
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ABA EFEITOS */}
          {activeTab === 'efeitos' && selectedImage && !isBaseLayerSelected && (
            <div className="h-full overflow-y-auto">
              {/* Aviso sobre WebGL em mobile */}
              {!webglSupported && showWebGLWarning && (
                <div className="p-4 bg-yellow-900 border-b border-yellow-700">
                  <div className="flex items-start space-x-3">
                    <svg className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <div className="flex-1">
                      <h4 className="text-sm font-medium text-yellow-200">Limitação do Dispositivo</h4>
                      <p className="text-xs text-yellow-300 mt-1">
                        Alguns efeitos podem não funcionar corretamente neste dispositivo devido a limitações de WebGL. 
                        Recomendamos usar filtros e ajustes que têm melhor compatibilidade mobile.
                      </p>
                      <button
                        onClick={() => setShowWebGLWarning(false)}
                        className="text-xs text-yellow-200 underline mt-2"
                      >
                        Entendi, não mostrar novamente
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Nível 1: Nenhum efeito selecionado - Mostrar seletor */}
              {activeEffectType === null && (
                <EffectSelector
                  onSelect={(type) => setActiveEffectType(type)}
                  onClose={() => {
                    closeEffectsPanel();
                  }}
                />
              )}

              {/* Nível 2: Efeito selecionado - Mostrar controles genéricos */}
              {activeEffectType && activeEffectType !== 'adjustments' && (
                <GenericEffectControls
                  effectType={activeEffectType}
                  selectedEffect={selectedEffect}
                  onSelectEffect={setSelectedEffect}
                  settings={effectSettings?.[activeEffectType] || {}}
                  onUpdateSettings={(newSettings) => updateEffectSettings(activeEffectType, newSettings)}
                  onApply={() => applyEffect(activeEffectType)}
                  onBack={() => {
                    setActiveEffectType(null);
                    setSelectedEffect(null);
                  }}
                />
              )}
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default MobileControlsPanel;


